import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { canModifyDeal } from '../../utils/permissions';
import RefundDetailsModal from './RefundDetailsModal';

// Move currencies outside of component to prevent recreation on each render
const currencies = {
  'Norway': 'NOK',
  'Sweden': 'SEK',
  'Germany': 'EUR',
  'Switzerland': 'CHF',
  'Luxembourg': 'EUR',
  'Austria': 'EUR',
  'United States': 'USD'
};

const countries = [
  'Norway',
  'Sweden',
  'Germany',
  'Switzerland',
  'Luxembourg',
  'United States',
  'Austria'
].sort();

const ViewEditDealModal = ({ isOpen, onClose, onDealUpdated, dealId }) => {
  const [formData, setFormData] = useState({
    title: '',
    value: '',
    currency: '',
    stage_id: '',
    payment_method: 'Bank Transfer',
    country: '',
    course: '',
    course_id: '',
    paid_date: '',
    created_at: '',
    notes: '',
    student_name: '',
    student_age: '',
    deal_owner_id: '',
    refunded_amount: ''
  });
  const [canEdit, setCanEdit] = useState(false);
  
  const [lead, setLead] = useState(null);
  const [stages, setStages] = useState([]);
  const [coursesList, setCoursesList] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [dealOwner, setDealOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [generateInvoice, setGenerateInvoice] = useState(false);
  const [invoiceDueDays, setInvoiceDueDays] = useState(14);
  const [invoice, setInvoice] = useState(null);
  const [diploma, setDiploma] = useState(null);
  const [showRefundDetails, setShowRefundDetails] = useState(false);
  
  const paymentMethods = [
    'Bank Transfer',
    'Stripe',
    'SVEA'
  ];
  
  // Courses will be dynamically loaded from the database
  
  // Fetch deal data and pipeline stages when modal opens
  useEffect(() => {
    // Skip effect if modal is closed or no dealId is provided
    if (!isOpen || !dealId) return;
    
    // Using a ref to track mounted state
    let isMounted = true;
    
    // Set loading state and clear errors
    setLoading(true);
    setError(null);
    
    // Important: Save the current edit mode to restore it after data fetch
    const currentEditingMode = isEditing;
    
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          if (isMounted) {
            setError('Authentication token not found');
            setLoading(false);
          }
          return;
        }
        
        const config = {
          headers: {
            'x-auth-token': token
          }
        };
        
        // Get current user from localStorage only once
        let user = null;
        const userJson = localStorage.getItem('user');
        if (userJson) {
          user = JSON.parse(userJson);
        } else {
          // Fetch current user if not in localStorage
          const userRes = await axios.get('/api/auth/me', config);
          user = userRes.data;
          localStorage.setItem('user', JSON.stringify(user));
        }
        
        // Fetch all data in parallel to reduce API calls
        const [dealRes, stagesRes, coursesRes, usersRes, invoicesRes, diplomasRes] = await Promise.all([
          axios.get('/api/deals/' + dealId, config),
          axios.get('/api/deals/stages/all', config),
          axios.get('/api/settings/courses/active', config),
          axios.get('/api/auth/users', config), // Fetch all users to include superusers
          axios.get('/api/deals/invoices/for-deal/' + dealId, config).catch(err => {
            // Handle case where deal has no invoices (this endpoint might return 404)
            console.log('No invoices found for deal or endpoint not available');
            return { data: [] };
          }),
          // Try to fetch existing diploma for the deal
          axios.get('/api/diplomas', { 
            ...config, 
            params: { dealId } 
          }).catch(err => {
            // Handle case where deal has no diplomas or endpoint not available
            console.log('No diplomas found for deal or endpoint not available');
            return { data: { diplomas: [] } };
          })
        ]);
        
        // Exit early if component unmounted during API calls
        if (!isMounted) return;
        
        const deal = dealRes.data;
        
        // Check if the user can modify this deal
        const userCanModify = canModifyDeal('update', deal);
        setCanEdit(userCanModify);
        
        // Format dates for the form
        let formattedPaidDate = '';
        if (deal.paid_date) {
          const date = new Date(deal.paid_date);
          formattedPaidDate = date.toISOString().split('T')[0]; // YYYY-MM-DD format
        }
        
        let formattedCreatedAt = '';
        if (deal.created_at) {
          const date = new Date(deal.created_at);
          formattedCreatedAt = date.toISOString().split('T')[0]; // YYYY-MM-DD format
        }
        
        // Filter out users with sales or superuser role for the deal owner dropdown
        const eligibleUsers = usersRes.data.filter(
          u => u.role === 'sales' || u.role === 'superuser'
        );
        
        // Build all state updates
        let owner = null;
        
        // If there's a deal owner, find their details
        if (deal.deal_owner_id) {
          owner = eligibleUsers.find(u => u.id === parseInt(deal.deal_owner_id));
          if (!owner) {
            owner = { name: deal.owner_name || 'Unknown', id: deal.deal_owner_id };
          }
        } else if (deal.created_by) {
          // If no owner, default to the creator
          owner = eligibleUsers.find(u => u.id === parseInt(deal.created_by));
          if (!owner) {
            owner = { name: 'Deal Creator', id: deal.created_by };
          }
        }
        
        // Set lead information
        const leadData = deal.lead_id ? {
          id: deal.lead_id,
          name: deal.lead_name || 'Unknown',
          email: deal.lead_email || ''
        } : {
          id: null,
          name: 'No customer assigned',
          email: ''
        };
        
        // Exit early if component unmounted
        if (!isMounted) return;
        
        // Get invoice information if available
        // Make sure we have valid invoice data before setting it
        const invoiceData = invoicesRes.data && 
                           Array.isArray(invoicesRes.data) && 
                           invoicesRes.data.length > 0 && 
                           invoicesRes.data[0] && 
                           invoicesRes.data[0].id
          ? invoicesRes.data[0] // Use the most recent invoice
          : null;
        
        // Get diploma information if available
        // Make sure we have valid diploma data before setting it
        const diplomaData = diplomasRes.data && 
                           diplomasRes.data.diplomas && 
                           Array.isArray(diplomasRes.data.diplomas) && 
                           diplomasRes.data.diplomas.length > 0 &&
                           diplomasRes.data.diplomas.find(d => d.deal_id === parseInt(dealId))
          ? diplomasRes.data.diplomas.find(d => d.deal_id === parseInt(dealId)) // Find diploma for this deal
          : null;
        
        // Update all state at once to minimize renders
        setCurrentUser(user);
        setSalesUsers(eligibleUsers);
        setDealOwner(owner);
        setStages(stagesRes.data || []);
        setCoursesList(coursesRes.data || []);
        setInvoice(invoiceData);
        setDiploma(diplomaData);
        setLead(leadData);
        
        // Debug the API response
        console.log('Deal API response:', deal);
        
        // Set form data only once
        setFormData({
          lead_id: deal.lead_id,
          title: deal.title || '',
          value: deal.value || '',
          currency: deal.currency || '',
          stage_id: deal.stage_id || '',
          payment_method: deal.payment_method || 'Bank Transfer',
          country: deal.country || '',
          course: deal.course || '',
          course_id: deal.course_id || '',
          paid_date: formattedPaidDate,
          created_at: formattedCreatedAt,
          notes: deal.notes || '',
          student_name: deal.student_name || '',
          student_age: deal.student_age || '',
          deal_owner_id: deal.deal_owner_id || deal.created_by || '',
          refunded_amount: deal.refunded_amount || '',
          original_source: deal.original_source || '',
          latest_source: deal.latest_source || '',
          // Add lesson tracking fields
          total_lessons: deal.total_lessons || 0,
          completed_lessons: deal.completed_lessons || 0,
          attended_lessons: deal.attended_lessons || 0,
          lesson_start_date: deal.lesson_start_date || ''
        });
        
        setLoading(false);
        
        // Restore the editing mode state after data is loaded
        // This prevents the edit mode from being reset when data is loaded
        if (currentEditingMode) {
          setTimeout(() => {
            setIsEditing(currentEditingMode);
          }, 10);
        }
        
        // Ensure all buttons are properly enabled
        setTimeout(() => {
          // Re-enable edit button if it exists
          const editButton = document.getElementById('edit-button');
          if (editButton) {
            editButton.disabled = false;
            console.log("Edit button enabled on data load");
          }
        }, 100);
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching deal data:', err);
          setError('Failed to load deal data: ' + (err.response?.data?.message || err.message));
          setLoading(false);
        }
      }
    };
    
    // Execute fetch only once
    fetchData();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [isOpen, dealId]); // Only depend on isOpen and dealId - currencies is defined outside component now
  
  // Update currency based on country selection
  // This effect is temporarily disabled to prevent infinite loops
  /*
  useEffect(() => {
    if (formData.country && currencies[formData.country] && isEditing) {
      // Only update currency if it's different to avoid infinite loop
      if (formData.currency !== currencies[formData.country]) {
        setFormData(prev => ({
          ...prev,
          currency: currencies[formData.country]
        }));
      }
    }
  }, [formData.country, formData.currency, isEditing, currencies]);
  */
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Create the updated form data
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    
    // If country is changed and we're in edit mode, update the currency
    if (name === 'country' && currencies[value] && isEditing && currencies[value] !== formData.currency) {
      updatedFormData.currency = currencies[value];
    }
    
    setFormData(updatedFormData);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent form submission if we're not in edit mode
    if (!isEditing) {
      console.log("Not in edit mode, ignoring form submission");
      return;
    }
    
    // Get the submit button and disable it to prevent double submission
    const submitButton = document.getElementById('save-changes-button');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Saving...';
    }
    
    try {
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found');
        return;
      }
      
      // Make sure we have all required fields
      const dataToSubmit = {
        ...formData,
        // Ensure these fields have valid values, not undefined
        student_name: formData.student_name || '',
        student_age: formData.student_age || null,
        currency: formData.currency || 'USD',
        payment_method: formData.payment_method || 'Bank Transfer',
        // Handle refunded amount
        refunded_amount: formData.refunded_amount || null
      };
      
      console.log('Submitting deal update:', dataToSubmit);
      
      const config = {
        headers: {
          'x-auth-token': token
        }
      };
      
      const res = await axios.put('/api/deals/' + dealId, dataToSubmit, config);
      
      // Exit edit mode first
      setIsEditing(false);
      
      // Then update the parent component
      onDealUpdated(res.data);
      
      // Generate invoice if checkbox is checked
      if (generateInvoice) {
        try {
          const invoiceConfig = {
            headers: {
              'x-auth-token': token,
              'Content-Type': 'application/json'
            }
          };
          
          // Calculate due date from days
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + invoiceDueDays);
          
          // Create invoice for the deal
          const invoiceResponse = await axios.post(
            '/api/deals/invoices/from-deal/' + dealId, 
            { due_date: dueDate.toISOString() }, 
            invoiceConfig
          );
          
          // Get the invoice ID from the response
          const invoiceId = invoiceResponse.data.id;
          
          // Update the invoice state
          setInvoice(invoiceResponse.data);
          
          // Reset the generate invoice checkbox
          setGenerateInvoice(false);
          
          // Open the PDF in a new tab
          window.open('/api/deals/invoices/' + invoiceId + '/pdf?token=' + token, '_blank');
        } catch (err) {
          console.error('Error generating invoice:', err);
          setError('Failed to generate invoice: ' + (err.response?.data?.message || err.message));
        }
      }
      
      // Reset any disabled buttons
      setTimeout(() => {
        // Find any disabled buttons and re-enable them
        document.querySelectorAll('button[disabled]').forEach(button => {
          button.disabled = false;
        });
      }, 300);
    } catch (err) {
      console.error('Error updating deal:', err);
      // Log more detailed error information
      if (err.response) {
        console.error('Error response:', err.response.status, err.response.data);
      }
      setError('Failed to update deal: ' + (err.response?.data?.message || err.message));
      
      // Re-enable the submit button on error
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'Save Changes';
      }
    }
  };
  
  // Use a more stable way to toggle edit mode
  const toggleEditMode = () => {
    // Prevent switching to edit mode if we're already in edit mode
    if (isEditing) {
      console.log("Already in edit mode, ignoring toggle request");
      return;
    }
    
    // Check permission before enabling edit mode
    if (!canEdit) {
      setError("You don't have permission to edit this deal.");
      return;
    }
    
    console.log("Enabling edit mode");
    
    // Force true rather than toggling to ensure it stays in edit mode
    setIsEditing(true);
    // Reset error when toggling edit mode
    setError(null);
    
    // Add a small delay to ensure the state update completes
    setTimeout(() => {
      console.log("Edit mode should be enabled now, isEditing:", true);
    }, 20);
  };
  
  const cancelEdit = () => {
    // Exit edit mode first, then reset form data
    setIsEditing(false);
    // Reset generate invoice checkbox
    setGenerateInvoice(false);
    
    // Refetch the data to reset form
    if (dealId) {
      const fetchData = async () => {
        try {
          const token = localStorage.getItem('token');
          const config = {
            headers: {
              'x-auth-token': token
            }
          };
          
          const dealRes = await axios.get('/api/deals/' + dealId, config);
          const deal = dealRes.data;
          
          // Format dates for the form
          let formattedPaidDate = '';
          if (deal.paid_date) {
            const date = new Date(deal.paid_date);
            formattedPaidDate = date.toISOString().split('T')[0]; // YYYY-MM-DD format
          }
          
          let formattedCreatedAt = '';
          if (deal.created_at) {
            const date = new Date(deal.created_at);
            formattedCreatedAt = date.toISOString().split('T')[0]; // YYYY-MM-DD format
          }
          
          // Update form data after a small delay to ensure edit mode is off
          setTimeout(() => {
            setFormData({
              lead_id: deal.lead_id,
              title: deal.title || '',
              value: deal.value || '',
              currency: deal.currency || '',
              stage_id: deal.stage_id || '',
              payment_method: deal.payment_method || 'Bank Transfer',
              country: deal.country || '',
              course: deal.course || '',
              course_id: deal.course_id || '',
              paid_date: formattedPaidDate,
              created_at: formattedCreatedAt,
              notes: deal.notes || '',
              student_name: deal.student_name || '',
              student_age: deal.student_age || '',
              deal_owner_id: deal.deal_owner_id || deal.created_by || '',
              refunded_amount: deal.refunded_amount || '',
              original_source: deal.original_source || '',
              latest_source: deal.latest_source || ''
            });
            
            // Re-enable all buttons
            setTimeout(() => {
              // Find any disabled buttons and re-enable them
              document.querySelectorAll('button[disabled]').forEach(button => {
                button.disabled = false;
              });
              
              // Specifically check for the edit button
              const editButton = document.getElementById('edit-button');
              if (editButton) {
                editButton.disabled = false;
                console.log("Edit button re-enabled after cancel");
              }
            }, 100);
          }, 10);
        } catch (err) {
          console.error('Error reloading deal data:', err);
        }
      };
      
      fetchData();
    }
  };
  
  const formatCurrency = (amount, currency) => {
    if (!amount) return '-';
    
    const formatOptions = {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2
    };
    
    return new Intl.NumberFormat('en-US', formatOptions).format(amount);
  };
  
  // Handle ESC key press
  const handleEscKey = useCallback((event) => {
    if (event.key === 'Escape' && isOpen) {
      onClose();
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    // Add event listener when modal is open
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    // Cleanup event listener when modal is closed
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, handleEscKey]);

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-semibold">
            {isEditing ? 'Edit Deal' : 'View Deal'}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {loading ? (
          <div className="p-4 text-center">Loading...</div>
        ) : error ? (
          <div className="p-4 text-center text-red-600">{error}</div>
        ) : (
          <form onSubmit={handleSubmit} className="p-3">
            {/* Top row with customer info and course */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Customer
                </label>
                <div className="p-2 bg-gray-100 rounded">
                  <div className="font-medium">{lead?.name || 'N/A'}</div>
                  <div className="text-sm text-gray-500">{lead?.email || ''}</div>
                  {formData.original_source && (
                    <div className="text-sm text-gray-500 mt-1">
                      <span className="font-medium">Original Source:</span> {formData.original_source}
                    </div>
                  )}
                  {formData.latest_source && (
                    <div className="text-sm text-gray-500">
                      <span className="font-medium">Latest Source:</span> {formData.latest_source}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Course
                </label>
                {isEditing ? (
                  <>
                    <select
                      name="course_id"
                      value={formData.course_id || ''}
                      onChange={(e) => {
                        // Get both course_id and course name when a course is selected
                        const selectedCourseId = e.target.value;
                        const selectedCourse = coursesList.find(c => c.id.toString() === selectedCourseId);
                        
                        setFormData({
                          ...formData, 
                          course_id: selectedCourseId,
                          course: selectedCourse ? selectedCourse.name : '' // Keep course name for backward compatibility
                        });
                      }}
                      className="shadow appearance-none border rounded w-full py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      required
                    >
                      <option value="">Select Course</option>
                      {coursesList.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.name} - {course.age_group} - Level {course.level} - {course.course_format}
                          {course.video_content ? ' (Video)' : ''}
                        </option>
                      ))}
                    </select>
                    <div className="mt-1 text-xs text-blue-600">
                      <strong>Note:</strong> Assigning a course with video content will grant the customer access to those videos
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-2 bg-gray-100 rounded">
                      {formData.course || '-'}
                    </div>
                    {coursesList.find(c => c.id.toString() === formData.course_id)?.video_content && (
                      <div className="mt-1 text-xs text-blue-600 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Customer has access to video content
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            
            {/* Second row with country and title */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Country
                </label>
                {isEditing ? (
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="p-2 bg-gray-100 rounded">
                    {formData.country || '-'}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Title
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                ) : (
                  <div className="p-2 bg-gray-100 rounded">
                    {formData.title || '-'}
                  </div>
                )}
              </div>
            </div>
            
            {/* Third row with student info */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Student Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="student_name"
                    value={formData.student_name}
                    onChange={handleChange}
                    placeholder="Name of the student"
                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                ) : (
                  <div className="p-2 bg-gray-100 rounded">
                    {formData.student_name || '-'}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Student Age
                </label>
                {isEditing ? (
                  <input
                    type="number"
                    name="student_age"
                    value={formData.student_age}
                    onChange={handleChange}
                    placeholder="Age"
                    min="0"
                    max="120"
                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                ) : (
                  <div className="p-2 bg-gray-100 rounded">
                    {formData.student_age || '-'}
                  </div>
                )}
              </div>
            </div>
            
            {/* Fourth row with price and payment method */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Price
                </label>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    name="value"
                    value={formData.value}
                    onChange={handleChange}
                    placeholder={`Amount in ${formData.currency || 'currency'}`}
                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                ) : (
                  <div className="p-2 bg-gray-100 rounded">
                    {formatCurrency(formData.value, formData.currency)}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Currency
                </label>
                <div className="p-2 bg-gray-100 rounded">
                  {formData.currency || '-'}
                </div>
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Payment Method
                </label>
                {isEditing ? (
                  <select
                    name="payment_method"
                    value={formData.payment_method}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  >
                    {paymentMethods.map(method => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="p-2 bg-gray-100 rounded">
                    {formData.payment_method || '-'}
                  </div>
                )}
              </div>
            </div>
            
            {/* Fifth row with Deal Stage, Owner, and Dates */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Deal Stage
                </label>
                {isEditing ? (
                  <select
                    name="stage_id"
                    value={formData.stage_id}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  >
                    <option value="">Select Stage</option>
                    {Array.isArray(stages) ? stages.map(stage => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    )) : <option value="">No stages available</option>}
                  </select>
                ) : (
                  <div className="p-2 bg-gray-100 rounded">
                    {stages.find(s => s.id === parseInt(formData.stage_id))?.name || '-'}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Deal Owner
                </label>
                {isEditing && currentUser && currentUser.role === 'superuser' ? (
                  <select
                    name="deal_owner_id"
                    value={formData.deal_owner_id}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  >
                    <option value="">Select Deal Owner</option>
                    {salesUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.role})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="p-2 bg-gray-100 rounded">
                    {dealOwner ? dealOwner.name : '-'}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Created On
                </label>
                <div className="p-2 bg-gray-100 rounded">
                  {formData.created_at ? new Date(formData.created_at).toLocaleDateString() : '-'}
                </div>
              </div>
            </div>
            
            {/* Add a row for Payment Date */}
            {formData.paid_date && (
              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Payment Received On
                </label>
                <div className="p-2 bg-gray-100 rounded">
                  {formData.paid_date ? new Date(formData.paid_date).toLocaleDateString() : '-'}
                </div>
              </div>
            )}
            
            {/* Video Course Access Link - only show when course is video format */}
            {console.log('Course info for video links:', {
              formDataCourseId: formData.course_id,
              foundCourse: coursesList.find(c => c.id.toString() === formData.course_id),
              courseFormat: coursesList.find(c => c.id.toString() === formData.course_id)?.course_format,
              allCourses: coursesList.map(c => ({ id: c.id, name: c.name, format: c.course_format }))
            })}
            
            {/* Show links for courses with video content - case-insensitive check */}
            {(formData.course_id && coursesList.find(c => {
              // Handle course ID type conversion
              const courseId = c.id.toString();
              const formCourseId = formData.course_id.toString();
              
              // Check if IDs match and then check format
              return courseId === formCourseId && 
                (c.course_format?.toLowerCase() === 'video' || 
                 c.video_content === true || 
                 c.format?.toLowerCase() === 'video');
            })) && (
              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Video Course Access
                </label>
                <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-blue-800 text-sm font-medium overflow-hidden overflow-ellipsis">
                        <strong>Course Access Link:</strong><br />
                        {window.location.origin}/video-course.html?dealId={dealId}
                      </div>
                    </div>
                    <div className="flex ml-2">
                      <button
                        type="button"
                        onClick={() => {
                          const accessLink = `${window.location.origin}/video-course.html?dealId=${dealId}`;
                          navigator.clipboard.writeText(accessLink)
                            .then(() => {
                              // Show a small tooltip or message
                              const button = document.getElementById('copy-link-button');
                              if (button) {
                                const originalText = button.innerText;
                                button.innerText = 'Copied!';
                                setTimeout(() => {
                                  button.innerText = originalText;
                                }, 2000);
                              }
                            })
                            .catch(err => {
                              console.error('Could not copy link: ', err);
                            });
                        }}
                        id="copy-link-button"
                        className="bg-blue-600 text-white px-2 py-1 text-xs rounded hover:bg-blue-700"
                      >
                        Copy Link
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const courseName = coursesList.find(c => c.id.toString() === formData.course_id)?.name || "your course";
                          const accessLink = `${window.location.origin}/video-course.html?dealId=${dealId}`;
                          const leadName = lead?.name || "there";
                          const studentName = formData.student_name || leadName;
                          
                          // Create email template
                          const emailTemplate = `Subject: Your Video Course Access - ${courseName}

Hi ${leadName},

Thank you for your purchase! The video course for ${studentName} is now ready to access.

Course: ${courseName}
Access Link: ${accessLink}

Simply click the link above (or copy and paste it into your browser) to start watching the course videos.

This link is for your personal use and provides full access to all course materials.

If you have any questions or need assistance, please don't hesitate to contact us.

Best regards,
${currentUser?.name || 'The Team'}`;
                          
                          navigator.clipboard.writeText(emailTemplate)
                            .then(() => {
                              const button = document.getElementById('copy-email-button');
                              if (button) {
                                const originalText = button.innerText;
                                button.innerText = 'Copied!';
                                setTimeout(() => {
                                  button.innerText = originalText;
                                }, 2000);
                              }
                            })
                            .catch(err => {
                              console.error('Could not copy email template: ', err);
                            });
                        }}
                        id="copy-email-button"
                        className="ml-2 bg-green-600 text-white px-2 py-1 text-xs rounded hover:bg-green-700"
                      >
                        Copy Email
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    Share this link with the customer to access their video course
                  </p>
                </div>
              </div>
            )}
            
            {/* Lesson Progress Indicator */}
            {formData.course_id && (
              <div className="mb-3">
                <label className="block text-gray-700 text-sm font-bold mb-1">
                  Lesson Progress
                </label>
                <div className="p-3 bg-gray-100 rounded">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm">
                      <span className="font-medium">Total Lessons:</span> {formData.total_lessons || 0}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Completed:</span> {formData.completed_lessons || 0}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Remaining:</span> {(formData.total_lessons || 0) - (formData.completed_lessons || 0)}
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full" 
                      style={{ 
                        width: `${formData.total_lessons ? 
                          Math.min(100, Math.round((formData.completed_lessons || 0) / formData.total_lessons * 100)) : 0}%` 
                      }}
                    ></div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-1">
                    {formData.lesson_start_date && (
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">Course Start:</span> {new Date(formData.lesson_start_date).toLocaleDateString()}
                      </div>
                    )}
                    <div className="text-xs text-gray-500">
                      {formData.total_lessons ? 
                        `${Math.min(100, Math.round((formData.completed_lessons || 0) / formData.total_lessons * 100))}% complete` : 
                        '0% complete'}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Notes */}
            <div className="mb-3">
              <label className="block text-gray-700 text-sm font-bold mb-1">
                Notes
              </label>
              {isEditing ? (
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="shadow appearance-none border rounded w-full py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  rows={2}
                />
              ) : (
                <div className="p-2 bg-gray-100 rounded min-h-[50px] whitespace-pre-wrap">
                  {formData.notes || '-'}
                </div>
              )}
            </div>
            
            {/* Refund Amount - Only show if stage is "Refunded" */}
            {stages.find(s => s.id === parseInt(formData.stage_id))?.name === 'Refunded' && (
              <div className="mb-3">
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-gray-700 text-sm font-bold">
                    Refund Amount
                  </label>
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={() => setShowRefundDetails(true)}
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                    >
                      View Refund Details
                    </button>
                  )}
                </div>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    name="refunded_amount"
                    value={formData.refunded_amount || ''}
                    onChange={handleChange}
                    className="shadow appearance-none border rounded w-full py-1.5 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  />
                ) : (
                  <div className="p-2 bg-gray-100 rounded">
                    {formatCurrency(formData.refunded_amount, formData.currency) || '-'}
                  </div>
                )}
              </div>
            )}
            
            {/* Generate Invoice Option (Only in Edit Mode) */}
            {isEditing && (
              <div className="mb-3">
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="generateInvoice"
                    checked={generateInvoice}
                    onChange={() => setGenerateInvoice(!generateInvoice)}
                    className="mr-2 h-4 w-4 text-blue-600"
                  />
                  <label htmlFor="generateInvoice" className="text-gray-700">
                    Generate PDF invoice after saving
                  </label>
                </div>
                {generateInvoice && (
                  <div className="ml-6 flex items-center">
                    <label htmlFor="invoiceDueDays" className="text-gray-700 mr-2">
                      Due in:
                    </label>
                    <input
                      type="number"
                      id="invoiceDueDays"
                      min="1"
                      max="365"
                      value={invoiceDueDays}
                      onChange={(e) => setInvoiceDueDays(parseInt(e.target.value) || 14)}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                    />
                    <span className="text-gray-700 ml-1">days</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Due Date Selection for Generate Invoice (Only in View Mode when no invoice exists) */}
            {!isEditing && (!invoice || !invoice.id) && (
              <div className="mb-3 flex items-center justify-end">
                <label htmlFor="invoiceDueDaysView" className="text-gray-700 mr-2 text-sm">
                  Invoice due in:
                </label>
                <input
                  type="number"
                  id="invoiceDueDaysView"
                  min="1"
                  max="365"
                  value={invoiceDueDays}
                  onChange={(e) => setInvoiceDueDays(parseInt(e.target.value) || 14)}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                />
                <span className="text-gray-700 ml-1 text-sm">days</span>
              </div>
            )}
            
            <div className="flex justify-end">
              {isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="bg-gray-500 text-white px-3 py-1.5 rounded-md hover:bg-gray-600 mr-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 text-sm"
                    // Add pointer event delay to prevent double clicks
                    style={{ pointerEvents: 'auto' }}
                    id="save-changes-button"
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={onClose}
                    className="bg-gray-500 text-white px-3 py-1.5 rounded-md hover:bg-gray-600 mr-2 text-sm"
                  >
                    Close
                  </button>
                  {/* Invoice-related buttons */}
                  {invoice && invoice.id ? (
                    <button
                      type="button"
                      onClick={() => {
                        const token = localStorage.getItem('token');
                        if (!token) {
                          setError('Authentication token not found');
                          return;
                        }
                        // Use the blob approach for PDF download that we implemented elsewhere
                        axios({
                          url: `/api/deals/invoices/${invoice.id}/pdf`,
                          method: 'GET',
                          responseType: 'blob',
                          headers: {
                            'x-auth-token': token
                          }
                        }).then((response) => {
                          const file = new Blob([response.data], { type: 'application/pdf' });
                          const fileURL = URL.createObjectURL(file);
                          const fileLink = document.createElement('a');
                          fileLink.href = fileURL;
                          fileLink.setAttribute('download', `invoice-${invoice.invoice_number || invoice.id}.pdf`);
                          document.body.appendChild(fileLink);
                          fileLink.click();
                          document.body.removeChild(fileLink);
                          URL.revokeObjectURL(fileURL);
                        }).catch(error => {
                          console.error('Error downloading PDF:', error);
                          setError('Failed to download PDF. Please try again.');
                        });
                      }}
                      className="bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 mr-2 text-sm"
                    >
                      View Invoice
                    </button>
                  ) : (
                    /* Always show Generate Invoice button when there's no invoice */
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          // Get token from localStorage for authentication
                          const token = localStorage.getItem('token');
                          if (!token) {
                            setError('Authentication token not found');
                            return;
                          }
                          
                          const config = {
                            headers: {
                              'x-auth-token': token,
                              'Content-Type': 'application/json'
                            }
                          };
                          
                          // Calculate due date from days
                          const dueDate = new Date();
                          dueDate.setDate(dueDate.getDate() + invoiceDueDays);
                          
                          // Create invoice for the deal
                          const response = await axios.post(
                            '/api/deals/invoices/from-deal/' + dealId, 
                            { due_date: dueDate.toISOString() }, 
                            config
                          );
                          
                          // Get the invoice ID from the response
                          const invoiceId = response.data.id;
                          
                          // Update the invoice state immediately
                          setInvoice(response.data);
                          // Download the PDF using the blob approach that works elsewhere
                          axios({
                            url: `/api/deals/invoices/${invoiceId}/pdf`,
                            method: 'GET',
                            responseType: 'blob',
                            headers: {
                              'x-auth-token': token
                            }
                          }).then((pdfResponse) => {
                            const file = new Blob([pdfResponse.data], { type: 'application/pdf' });
                            const fileURL = URL.createObjectURL(file);
                            const fileLink = document.createElement('a');
                            fileLink.href = fileURL;
                            fileLink.setAttribute('download', `invoice-${response.data.invoice_number || invoiceId}.pdf`);
                            document.body.appendChild(fileLink);
                            fileLink.click();
                            document.body.removeChild(fileLink);
                            URL.revokeObjectURL(fileURL);
                          }).catch(pdfError => {
                            console.error('Error downloading PDF:', pdfError);
                            setError('Failed to download PDF. Please try again.');
                          });
                          
                          // Force a refresh of the deal data to ensure everything is up to date
                          setTimeout(() => {
                            const fetchInvoiceData = async () => {
                              try {
                                const invoicesRes = await axios.get('/api/deals/invoices/for-deal/' + dealId, config);
                                if (invoicesRes.data && invoicesRes.data.length > 0) {
                                  setInvoice(invoicesRes.data[0]);
                                }
                              } catch (err) {
                                console.error('Error refreshing invoice data:', err);
                              }
                            };
                            fetchInvoiceData();
                          }, 500);
                        } catch (err) {
                          console.error('Error generating invoice:', err);
                          setError('Failed to generate invoice: ' + (err.response?.data?.message || err.message));
                        }
                      }}
                      className="bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 mr-2 text-sm"
                    >
                      Generate Invoice
                    </button>
                  )}
                  
                  {/* Diploma button - only show when deal stage is "Course Complete" */}
                  {stages.find(s => s.id === parseInt(formData.stage_id))?.name === 'Course Complete' && (
                    diploma && diploma.id ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            const token = localStorage.getItem('token');
                            if (!token) {
                              setError('Authentication token not found');
                              return;
                            }
                            // Download existing diploma PDF
                            axios({
                              url: `/api/diplomas/${diploma.id}/pdf`,
                              method: 'GET',
                              responseType: 'blob',
                              headers: {
                                'x-auth-token': token
                              }
                            }).then((response) => {
                              const file = new Blob([response.data], { type: 'application/pdf' });
                              const fileURL = URL.createObjectURL(file);
                              const fileLink = document.createElement('a');
                              fileLink.href = fileURL;
                              fileLink.setAttribute('download', `diploma-${diploma.diploma_number || diploma.id}.pdf`);
                              document.body.appendChild(fileLink);
                              fileLink.click();
                              document.body.removeChild(fileLink);
                              URL.revokeObjectURL(fileURL);
                            }).catch(error => {
                              console.error('Error downloading diploma PDF:', error);
                              setError('Failed to download diploma PDF. Please try again.');
                            });
                          }}
                          className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 mr-2 text-sm"
                        >
                          View Diploma
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              if (!token) {
                                setError('Authentication token not found');
                                return;
                              }
                              
                              const config = {
                                headers: {
                                  'x-auth-token': token,
                                  'Content-Type': 'application/json'
                                }
                              };
                              
                              // Send diploma by email
                              const response = await axios.post(
                                `/api/diplomas/${diploma.id}/email`, 
                                {}, // You could add a custom message here if needed
                                config
                              );
                              
                              // Show success message
                              setError(null);
                              alert('Diploma has been emailed to the client.');
                            } catch (err) {
                              console.error('Error emailing diploma:', err);
                              setError('Failed to email diploma: ' + (err.response?.data?.message || err.message));
                            }
                          }}
                          className="bg-blue-500 text-white px-3 py-1.5 rounded-md hover:bg-blue-600 mr-2 text-sm"
                        >
                          Email Diploma
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            // Get token from localStorage for authentication
                            const token = localStorage.getItem('token');
                            if (!token) {
                              setError('Authentication token not found');
                              return;
                            }
                            
                            const config = {
                              headers: {
                                'x-auth-token': token,
                                'Content-Type': 'application/json'
                              }
                            };
                            
                            // Create diploma for the deal
                            const response = await axios.post(
                              `/api/diplomas/from-deal/${dealId}`, 
                              {}, 
                              config
                            );
                            
                            // Get the diploma ID from the response
                            const diplomaId = response.data.id;
                            
                            // Update the diploma state immediately
                            setDiploma(response.data);
                            
                            // Download the PDF using the blob approach
                            axios({
                              url: `/api/diplomas/${diplomaId}/pdf`,
                              method: 'GET',
                              responseType: 'blob',
                              headers: {
                                'x-auth-token': token
                              }
                            }).then((pdfResponse) => {
                              const file = new Blob([pdfResponse.data], { type: 'application/pdf' });
                              const fileURL = URL.createObjectURL(file);
                              const fileLink = document.createElement('a');
                              fileLink.href = fileURL;
                              fileLink.setAttribute('download', `diploma-${response.data.diploma_number || diplomaId}.pdf`);
                              document.body.appendChild(fileLink);
                              fileLink.click();
                              document.body.removeChild(fileLink);
                              URL.revokeObjectURL(fileURL);
                            }).catch(pdfError => {
                              console.error('Error downloading diploma PDF:', pdfError);
                              setError('Failed to download diploma PDF. Please try again.');
                            });
                            
                            // Force a refresh of the diploma data
                            setTimeout(() => {
                              const fetchDiplomaData = async () => {
                                try {
                                  const diplomasRes = await axios.get('/api/diplomas', { 
                                    ...config, 
                                    params: { dealId } 
                                  });
                                  if (diplomasRes.data && 
                                      diplomasRes.data.diplomas && 
                                      diplomasRes.data.diplomas.length > 0) {
                                    const dealDiploma = diplomasRes.data.diplomas.find(d => d.deal_id === parseInt(dealId));
                                    if (dealDiploma) {
                                      setDiploma(dealDiploma);
                                    }
                                  }
                                } catch (err) {
                                  console.error('Error refreshing diploma data:', err);
                                }
                              };
                              fetchDiplomaData();
                            }, 500);
                          } catch (err) {
                            console.error('Error generating diploma:', err);
                            setError('Failed to generate diploma: ' + (err.response?.data?.message || err.message));
                          }
                        }}
                        className="bg-purple-600 text-white px-3 py-1.5 rounded-md hover:bg-purple-700 mr-2 text-sm"
                      >
                        Generate Diploma
                      </button>
                    )
                  )}
                  {/* Edit button */}
                  {canEdit && (
                    <button
                      type="button"
                      id="edit-button"
                      onClick={(e) => {
                        // Prevent event bubbling
                        e.stopPropagation();
                        
                        // Use button ID rather than event target, as event target could be a child element
                        const button = document.getElementById('edit-button');
                        if (button) button.disabled = true;
                        
                        console.log("Edit button clicked, disabling temporarily");
                        
                        // Toggle edit mode after a small delay
                        setTimeout(() => {
                          toggleEditMode();
                          
                          // Re-enable button after toggling
                          setTimeout(() => {
                            if (button) {
                              button.disabled = false;
                              console.log("Edit button re-enabled");
                            }
                          }, 500);
                        }, 50);
                      }}
                      className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 text-sm"
                    >
                      Edit
                    </button>
                  )}
                </>
              )}
            </div>
          </form>
        )}
      </div>
      
      {/* Refund Details Modal */}
      <RefundDetailsModal 
        isOpen={showRefundDetails}
        onClose={() => setShowRefundDetails(false)}
        dealId={dealId}
      />
    </div>
  );
};

export default ViewEditDealModal;